// Browser exception capture (#3161). Honeybadger's JS SDK is configured from
// meta tags the layout renders only when a browser API key is present in
// credentials (production); without a key this is a no-op, so development
// and test never report. Context is ids only — never names or content.
import Honeybadger from "@honeybadger-io/js";
import type { CombinedError, Operation } from "urql";

function meta(name: string): string | null {
  if (typeof document === "undefined") return null;
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute("content") || null;
}

export interface ErrorTrackingContext {
  user_id?: string;
  organisation_id?: string;
}

let enabled = false;

/** Returns true when the SDK was configured (a key was present). */
export function initErrorTracking(context: ErrorTrackingContext = {}): boolean {
  const apiKey = meta("honeybadger-js-api-key");
  if (!apiKey) return false;

  Honeybadger.configure({
    apiKey,
    environment: meta("honeybadger-environment") || "production",
    revision: meta("app-revision") || undefined,
    reportData: true,
  });
  Honeybadger.setContext({ ...context });
  enabled = true;
  return true;
}

/** Test seam: forget that the SDK was configured. */
export function resetErrorTracking(): void {
  enabled = false;
}

function operationName(operation: Operation): string | undefined {
  for (const definition of operation.query.definitions) {
    if (definition.kind === "OperationDefinition") return definition.name?.value;
  }
  return undefined;
}

/**
 * Reports a failed GraphQL operation (#3172). Wired into the urql client as
 * a mapExchange onError hook so a query that silently empties a page — the
 * "No candidates yet" lie — is at least visible to us. Only the operation
 * name, kind, error messages and paths go out: never variables or data.
 */
export function reportGraphqlError(error: CombinedError, operation: Operation): void {
  if (!enabled) return;

  Honeybadger.notify(error, {
    name: "GraphQLError",
    message: error.message,
    context: {
      operation_name: operationName(operation) ?? "(anonymous)",
      operation_kind: operation.kind,
      network_error: Boolean(error.networkError),
      graphql_errors: error.graphQLErrors.map((graphqlError) => ({
        message: graphqlError.message,
        path: graphqlError.path,
      })),
    },
  });
}
